import React, { useState, useMemo, useEffect } from 'react';
import { Input } from 'semantic-ui-react';
import { List, useListRef, type RowComponentProps } from 'react-window';

type Props = {
  label: string;
  list: number[];
  defaultValue: number;
  handleValuesSelection: Function;
  disabled: boolean;
  precision?: number;
};

const ROW_HEIGHT = 20;
const VISIBLE_ROWS = 12;

type Row = { value: number; text: string };

type RowProps = {
  rows: Row[];
  selected: number | null;
  disabled: boolean;
  onPick: (value: number) => void;
};

// Defined at module scope so react-window doesn't remount rows each render.
// Visual styling lives in App.css (.frequency-list-row*). Only react-window's
// per-row positioning style is applied inline (it must be).
function FrequencyRow({
  index,
  style,
  rows,
  selected,
  disabled,
  onPick,
}: RowComponentProps<RowProps>) {
  const row = rows[index];
  const isSelected = selected != null && row.value === selected;
  const className = [
    'frequency-list-row',
    isSelected ? 'frequency-list-row-selected' : '',
    disabled ? 'frequency-list-row-disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      style={style}
      className={className}
      role="option"
      aria-selected={isSelected}
      onClick={disabled ? undefined : () => onPick(row.value)}
    >
      {row.text}
    </div>
  );
}

export const TxFrequencyEndList: React.FunctionComponent<Props> = ({
  label,
  list,
  defaultValue,
  handleValuesSelection,
  disabled,
  precision = 3,
}: Props) => {
  const [searchText, setSearchText] = useState('');
  const listRef = useListRef(null);

  // Precompute value + display text once per list (the list reference is
  // memoised upstream so this only rebuilds when the data actually changes).
  // `precision` is a dep too: cnReach channels come back with a higher
  // display_precision, and a product change that keeps the same frequencies
  // would otherwise leave the row text formatted to the old precision.
  const allRows: Row[] = useMemo(
    () => list.map((f) => ({ value: f, text: f.toFixed(precision) })),
    [list, precision]
  );
  const rows: Row[] = useMemo(() => {
    if (searchText.length === 0) {
      return allRows;
    }
    return allRows.filter((r) => r.text.includes(searchText));
  }, [allRows, searchText]);

  const selectedIndex = useMemo(() => {
    if (defaultValue == null) {
      return -1;
    }
    return rows.findIndex((r) => r.value === defaultValue);
  }, [rows, defaultValue]);

  // Scroll the selected value into view when the modal opens (and whenever the
  // selection changes). Deferred to the next frame so react-window has measured
  // the list height before we ask it to scroll.
  useEffect(() => {
    if (selectedIndex < 0) {
      return;
    }
    const raf = requestAnimationFrame(() => {
      listRef.current?.scrollToRow({ index: selectedIndex, align: 'smart' });
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedIndex, listRef]);

  // Adapt a row click to the existing change-handler contract, which reads
  // event.target.value (see selectionHandler in TxFrequencyField.service).
  function onPick(value: number) {
    handleValuesSelection({ target: { value: String(value) } });
  }
  
  return (
    <div className="ui form">
      {label && (
        <div className="mb-1">
          <b>{label}</b>
        </div>
      )}
      <div className="searchForm mb-1">
        <Input
          placeholder="Filter..."
          fluid
          icon={{
            name: 'cancel',
            onClick: (e) => {
              e.preventDefault();
              if (searchText.length > 0) {
                setSearchText('');
              }
            },
            link: true,
            title: 'Clear filter',
          }}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
        />
      </div>
      <div
        className={
          disabled ? 'frequency-list frequency-list-disabled' : 'frequency-list'
        }
        // Height is derived from the row constants, so it stays inline.
        style={{ height: ROW_HEIGHT * VISIBLE_ROWS }}
        role="listbox"
        aria-label={label}
      >
        {rows.length > 0 && (
          <List
            listRef={listRef}
            rowComponent={FrequencyRow}
            rowCount={rows.length}
            rowHeight={ROW_HEIGHT}
            rowProps={{ rows, selected: defaultValue ?? null, disabled, onPick }}
            style={{ height: ROW_HEIGHT * VISIBLE_ROWS }}
          />
        )}
      </div>
    </div>
  );
};

export default TxFrequencyEndList;
